import { Box } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'

import { IconBar, type IconKey } from './IconBar'
import { ContextPane } from './ContextPane'
import { SidebarShell } from './SidebarShell'
import { useLineInbox } from './useLineInbox'

// 見出し行 (マーク + 段階ラベル + トグル) と枠は SidebarShell に切り出した。
// 段階ラベルの表と幅は従来どおりここからも引ける。
export { SIDEBAR_WIDTH_VALUE, STAGE_LABEL } from './SidebarShell'

interface SidebarProps {
  onToggle?: () => void
  // route 遷移時に呼ぶ callback (スマホ Drawer のクローズ用)。ContextPane へ委譲。
  onNavigate?: () => void
}

// URL から IconKey を派生する pure 関数 (= state を持たず、 useLocation の変化に追従).
// /portal/* なら 'settings'、 /homecare* なら 'homecare' (訪問のページ /homecare/visits/:id も)、
// /messages* なら 'messages'、 /knowledge* なら 'knowledge'、
// それ以外 (= /, /soap/:id, /recording/*) は 'home'.
const deriveActiveFromPath = (pathname: string): IconKey => {
  if (pathname.startsWith('/portal')) return 'settings'
  if (pathname.startsWith('/homecare')) return 'homecare'
  if (pathname.startsWith('/messages')) return 'messages'
  if (pathname.startsWith('/knowledge')) return 'knowledge'
  return 'home'
}

// 薬剤師の左ペイン: 見出し行の下に IconBar (ホーム / 在宅 / やり取り / マニュアル / 設定) と、
// 選択中の pane (最近の薬歴 / 在宅の患者 / LINE のやり取り / マニュアル / 設定) を積む。
// 在宅は α だけ (IconBar の alphaOnly)、マニュアルは β だけ (betaOnly)、やり取りは LINE 連携の設定が済んだ店舗だけに出す
// (gate は出さないで行う)。
export const Sidebar = ({ onToggle, onNavigate }: SidebarProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const active = deriveActiveFromPath(location.pathname)
  const lineInbox = useLineInbox()
  const keys: IconKey[] = lineInbox.isConfigured
    ? ['home', 'homecare', 'messages', 'knowledge', 'settings']
    : ['home', 'homecare', 'knowledge', 'settings']

  const handleChange = (key: IconKey) => {
    if (key === 'settings') navigate('/portal')
    if (key === 'homecare') navigate('/homecare')
    if (key === 'messages') navigate('/messages')
    if (key === 'knowledge') navigate('/knowledge')
    if (key === 'home') navigate('/')
  }

  return (
    <SidebarShell onToggle={onToggle}>
      <IconBar
        active={active}
        onChange={handleChange}
        keys={keys}
        unreadKeys={lineInbox.hasUnread ? ['messages'] : undefined}
      />
      {/*
        ここでは スクロールしない (= overflow:auto を持たない).
        持たせると 日付ナビ / 薬剤師フィルタ / 検索 まで一緒に流れ、 患者リストを数件送っただけで
        絞り込みへ戻れなくなる (特に文字サイズ「大」). どこを固定しどこを流すかは pane 側の
        責務なので、 ここは高さを配るだけの flex 列に徹する.
      */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ContextPane active={active} onNavigate={onNavigate} />
      </Box>
    </SidebarShell>
  )
}
